import { API_BASE_URL } from '../config';
import api from './axiosInstance';

/**
 * Obtiene notarías paginadas del backend.
 * @param {number} page - Número de página (comienza en 1)
 * @param {number} pageSize - Cantidad de registros por página
 * @param {string|boolean} status - 'Todos' | 'Activos' | 'Inactivos' o boolean para isActive
 * @param {boolean|null} hasConvenio - true solo con convenio, false solo sin, null todas
 * @param {string} search - Término de búsqueda (nombre, comuna, email, encargado)
 * @returns {Promise} - { page, pageSize, total, notaries }
 */
export async function getNotariesPaged(
  page = 1,
  pageSize = 10,
  status = 'Activos',
  hasConvenio = null,
  search = null,
  sortBy = null,
  desc = null
) {
  try {
    const params = {
      page,
      pageSize,
    };

    if (typeof status === 'boolean') {
      params.isActive = status;
    } else if (status && status !== 'Todos') {
      params.status = status;
    }

    if (hasConvenio !== null && hasConvenio !== undefined) {
      params.hasConvenio = hasConvenio;
    }

    if (search && search.trim()) {
      params.search = search.trim();
    }

    if (sortBy) {
      params.sortBy = sortBy;
    }

    if (desc !== null && desc !== undefined) {
      params.desc = desc;
    }

    const response = await api.get(`${API_BASE_URL}/Notarias/paged`, {
      headers: {
        'Content-Type': 'application/json',
      },
      params,
    });

    return {
      page: response.data.page,
      pageSize: response.data.pageSize,
      total: response.data.total,
      notaries: response.data.data || [],
    };
  } catch (error) {
    console.error('getNotariesPaged failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo obtener el listado de notarías.';
    throw error;
  }
}

/**
 * Lista plana de notarías activas (para selección manual en el despacho).
 * @param {AbortSignal} signal - opcional, para cancelar la request
 * @returns {Promise} - Array de notarías activas (con convenio primero)
 */
export async function getActiveNotaries(signal) {
  try {
    const response = await api.get(`${API_BASE_URL}/Notarias/active`, {
      headers: { 'Content-Type': 'application/json' },
      signal,
    });

    return response.data || [];
  } catch (error) {
    console.error('getActiveNotaries failed:', error);
    error.customMessage = 'No se pudo obtener el listado de notarías activas.';
    throw error;
  }
}

/**
 * Obtiene una notaría por ID.
 * @param {number} notaryId - ID de la notaría
 * @returns {Promise} - La notaría
 */
export async function getNotaryById(notaryId) {
  try {
    const response = await api.get(`${API_BASE_URL}/Notarias/${notaryId}`, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('getNotaryById failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo obtener la notaría.';
    throw error;
  }
}

/**
 * Crea una nueva notaría.
 * @param {object} notaryData - { name, comuna, region, address, phone, contactPerson, email, hasConvenio, notes }
 * @returns {Promise} - { Message, NotariaId }
 */
export async function createNotary(notaryData) {
  try {
    const payload = {
      name: notaryData.name || '',
      comuna: notaryData.comuna || null,
      region: notaryData.region || null,
      address: notaryData.address || null,
      phone: notaryData.phone || null,
      contactPerson: notaryData.contactPerson || null,
      email: notaryData.email || null,
      hasConvenio: notaryData.hasConvenio ?? false,
      notes: notaryData.notes || null,
    };

    const response = await api.post(`${API_BASE_URL}/Notarias/create`, payload, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('createNotary failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo crear la notaría.';
    throw error;
  }
}

/**
 * Actualiza una notaría existente.
 * @param {number} notaryId - ID de la notaría
 * @param {object} notaryData - { name, comuna, region, address, phone, contactPerson, email, hasConvenio, notes, isActive }
 * @returns {Promise}
 */
export async function updateNotary(notaryId, notaryData) {
  try {
    const payload = {
      name: notaryData.name || '',
      comuna: notaryData.comuna || null,
      region: notaryData.region || null,
      address: notaryData.address || null,
      phone: notaryData.phone || null,
      contactPerson: notaryData.contactPerson || null,
      email: notaryData.email || null,
      hasConvenio: notaryData.hasConvenio ?? false,
      notes: notaryData.notes || null,
      isActive: notaryData.isActive ?? true,
    };

    const response = await api.put(`${API_BASE_URL}/Notarias/${notaryId}`, payload, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('updateNotary failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo actualizar la notaría.';
    throw error;
  }
}

/**
 * Elimina una notaría (borrado lógico).
 * @param {number} notaryId - ID de la notaría a desactivar
 * @returns {Promise}
 */
export async function deleteNotary(notaryId) {
  try {
    const response = await api.delete(`${API_BASE_URL}/Notarias/${notaryId}`, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('deleteNotary failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo eliminar la notaría.';
    throw error;
  }
}
